import { faUser, faPhone, faHashtag, faEnvelope, faTag, faEye } from "@fortawesome/free-solid-svg-icons";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import { uuid } from "config/app-constants";
import { LegacyRef } from "react";
import { DatalistOnClickType, DatalistRowActionType } from "types/datalist-types";
import { PersonFilterInput, PersonSortInput, SortEnumType } from "types/graphql";
import { personListInfo, PersonType } from "types/person-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AppLink from "components/common/AppLink";
import { AppRoutes } from "config/router";
import { DatalistColumnDefinitions } from "utilities/datalist/datalist-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import HTMLCode from "components/common/HTMLCode";
import If from "components/common/If";

export type PersonDatalistPropsType = {
    onItemClick?: DatalistOnClickType<PersonType>
    name: string,
    actions?: DatalistRowActionType<PersonType>,
    fixedFilters?: PersonFilterInput,
    header?: DatalistPropsType<PersonType, PersonFilterInput, PersonSortInput>['header']
    datalistRef?: LegacyRef<Datalist<PersonType, PersonFilterInput, PersonSortInput>>
}

const PersonDatalist = (props: PersonDatalistPropsType) => {
    const context = useAppContext();

    const datalistOptions: DatalistPropsType<PersonType, PersonFilterInput, PersonSortInput> = {
        itemId: i => i.id,
        name: props.name,
        header: props.header,
        table: {
            columns: [
                {
                    ...DatalistColumnDefinitions.nameColumnDefinition(context),
                    className: 'sticky left-0'
                },
                {
                    name: 'tel',
                    title: 'fields.phone.name',
                    resolve(i) {
                        return i.contactInfo?.find(c => c.type === 'tel')?.value || '-';
                    }
                },
                {
                    onItemClick: false,
                    name: 'email',
                    title: 'fields.email.name',
                    resolve(i) {
                        return i.contactInfo?.find(c => c.type === 'email')?.value || '-';
                    }
                },
                {
                    onItemClick: false,
                    name: 'countryCode',
                    title: 'fields.country.name',
                    resolve(i) {
                        if (!i.country) return;
                        return <div className="flex gap-1">
                            <img src={i.country?.flag} className="max-w-4 max-h-4 my-auto" alt={i.country?.alpha3Code} />
                            <span>{i.country.name}</span>
                        </div>;
                    }
                },
                {
                    name: 'id',
                    title: 'fields.id.name',
                    resolve(i) {
                        return i.id
                    }
                }
            ],
            render: {
                card: i => {
                    return (
                        <div className="w-full border theme-border px-4 py-2 mb-4 hover:border-sunrise-300">
                            <div className="flex ">
                                <div className="min-w-max flex text-lg">
                                    <span><FontAwesomeIcon icon={faUser} /> {i.name}</span>
                                </div>
                                <div className="w-full text-right text-sm">
                                    <span><FontAwesomeIcon icon={faHashtag} /> {i.id}</span>
                                </div>
                            </div>
                            <div className={`text-sm pt-2 ${!i.contactInfo.length ? 'hidden' : ''}`}>
                                <ul className="pl-1 list">
                                    {i.contactInfo.map(c => {
                                        return (
                                            <li key={`${props.name}-contact-info-${c.id}`}>
                                                <span>
                                                    <FontAwesomeIcon icon={c.type === 'email' ? faEnvelope : faPhone} /> {c.value}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                            <If condition={i.country}>
                                <div className="flex text-sm">
                                    <img src={i.country?.flag} className="max-w-4 max-h-4 mt-1" alt={i.country?.name} />
                                    <HTMLCode />
                                    <span>{i.country?.name}</span>
                                </div>
                            </If>
                            <div className="text-sm float-right">
                                <AppLink to={AppRoutes.PERSON_VIEW} params={{ id: i.id }}>
                                    <FontAwesomeIcon icon={faEye} />
                                </AppLink>
                            </div>
                            <div className={`text-sm pt-2 ${!i.tags.length ? 'hidden' : ''}`}>
                                <div className="pl-1">
                                    <span>
                                        <FontAwesomeIcon icon={faTag} />
                                    </span>
                                    <span className="pl-1">
                                        <i>{i.tags.map(c => c.name).join(', ')}</i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    )
                },
                actions: props.actions
            }
        },
        onItemClick: props.onItemClick,
        dataSource: {
            request: async (v) => {
                var whereList: PersonFilterInput[] | undefined = [];

                if (v.filters) {
                    whereList.push(v.filters);
                }

                if (v.searchText) {
                    const personSearch: PersonFilterInput = {
                        or: [
                            {
                                name: {
                                    contains: v.searchText
                                }
                            },
                            {
                                normalizedName: {
                                    contains: v.searchText.toLowerCase()
                                }
                            },
                            {
                                contactInfo: {
                                    some: {
                                        value: {
                                            endsWith: v.searchText.replace(/\s/g, '')
                                        }
                                    }
                                }
                            },
                            {
                                tags: {
                                    some: {
                                        tag: {
                                            name: {
                                                contains: v.searchText
                                            }
                                        }
                                    }
                                }
                            }
                        ]
                    }

                    whereList.push(personSearch);
                }

                if (props.fixedFilters) {
                    whereList.push(props.fixedFilters);
                }

                let where: PersonFilterInput | undefined = undefined;
                if (whereList.length > 0) {
                    where = {
                        and: whereList
                    }
                }

                const request = await personListInfo({
                    skip: v.skip,
                    take: v.take,
                    order: v.sort,
                    where: whereList.length > 0 ? where : undefined
                });
                return request.data;
            },
            sort: [{
                name: 'name',
                direction: SortEnumType.Asc,
                id: uuid()
            }]
        }
    }

    return (
        <div className="w-full">
            <Datalist {...datalistOptions} ref={props.datalistRef} />
        </div>
    );
}

export default PersonDatalist;
